// @ts-strict
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as UrlTemplate from 'url-template';
import { firstValueFrom } from 'rxjs';
// @ts-ignore-next-line
import IdsGlobal from 'ids-enterprise-wc/components/ids-global/ids-global';
// @ts-ignore-next-line
import IdsLocale from 'ids-enterprise-wc/components/ids-locale/ids-locale';

type Messages = { [ key: string ]: { id: string, value: string } };

/**
 * Initialise the locale service BEFORE the root application component is
 * loaded.  Simply add the module to the root module, and the initialiser
 * will be automatically added.
 */
@Injectable({
  providedIn: 'root'
})
export class LMLocaleInitializerService {
  private defaultLocale = 'en-US';
  private bannerUrl: string;
  private culturesPath: string;
  private localeDataPath: string;

  /**
   * Constructor.
   *
   * @param locale the locale being displayed.
   * @param baseHref the base url of the web app
   */
  constructor(private http: HttpClient) {
    this.culturesPath = CulturesPath;
    this.localeDataPath = WCLocaleDataPath;
    this.bannerUrl = this.getBannerUrl(bannerSpecUrl);

    const delimiter = this.bannerUrl.indexOf('?') > -1 ? '&' : '?';
    this.bannerUrl += delimiter + 'requestType=localeData';
  }

  public getBannerUrl(url: string) {
    const urlCompiledTemplate = UrlTemplate.parse(url);
    return urlCompiledTemplate.expand({
      urlPdlOrDa: LandmarkContext.urlPdlOrDa,
      webApp: LandmarkContext.webApp
    });
  }

  /**
   * Initializes the locale for this web application.
   */
  async initialize(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      firstValueFrom(this.http.get(this.bannerUrl))
        .then((data: any) => {
          const locale = this.getLocale(data);

          // transform browserMessages into format for Soho.Locale
          const messages: Messages = {};
          data.browserMessages.forEach((message: { messageId: string, messageValue: string }) => {
            messages[ message.messageId ] = {
              id:    message.messageId,
              value: message.messageValue
            };
          });

          Promise.all([
            this.set4XLocale(locale, messages),
            this.setWCLocale(locale, messages)
          ])
            .then(() => resolve(true))
            .catch(() => reject(true));
        });
    });
  }

  private set4XLocale(locale: string, messages: Messages): Promise<boolean> {
    Soho.Locale.culturesPath = this.culturesPath;

    return new Promise((resolve, reject) => {
      Soho.Locale.set(locale)
        .done(() => {
          const output = 'Soho.Locale set successfully';
          let errorMsg = '';

          try {
            Soho.Locale.extendTranslations(Soho.Locale.currentLanguage.name, messages);
          } catch (e) {
            errorMsg = 'Unable to extend translations to Soho.Locale';
            console.error(errorMsg + ' ' + locale);
            console.error(e);
          }

          resolve(true);
          console.log([ ((errorMsg.length > 0) ? ' - ' + errorMsg : output), locale ]);
        })
        .fail(() => {
          console.log([ 'Locale set failed', locale ]);

          if (locale !== this.defaultLocale) {
            console.log([ 'Loading default locale', this.defaultLocale ]);

            Soho.Locale.set(this.defaultLocale)
              .done(() => {
                resolve(true);
                console.log([ 'Default Locale set successfully', this.defaultLocale ]);
              })
              .fail(() => {
                reject(true);
                console.log([ 'Default Locale set failed', this.defaultLocale ]);
              });
          }
        });
    });
  }

  private setWCLocale(locale: string, messages: Messages): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const idsLocale: IdsLocale = IdsGlobal.getLocale();

      // todo: once this ticket is merged we can use the localDataPath to retrieve WC locale artifacts
      //  https://github.com/infor-design/enterprise-wc/pull/1623
      idsLocale.localeDataPath = this.localeDataPath;

      idsLocale.setLocale(locale)
        .then(() => {
          const output = 'IDSLocale set successfully';
          let errorMsg = '';

          try {
            idsLocale.extendTranslations(idsLocale.language.name, messages);
          } catch (e) {
            errorMsg = 'Unable to extend translations to Soho.Locale';
            console.error(errorMsg + ' ' + locale);
            console.error(e);
          }

          resolve(true);
          console.log([ ((errorMsg.length > 0) ? ' - ' + errorMsg : output), locale ]);
        })
        .catch((error: any) => {
          console.log([ 'Locale set failed', locale, error ]);

          if (locale !== this.defaultLocale) {
            console.log([ 'Loading default locale', this.defaultLocale ]);

            idsLocale.setLocale(this.defaultLocale)
              .then(() => {
                resolve(true);
                console.log([ 'Default Locale set successfully', this.defaultLocale ]);
              })
              .catch(() => {
                reject(true);
                console.log([ 'Default Locale set failed', this.defaultLocale ]);
              });
          }
        });
    });
  }

  private getLocale(data: any): string {
    let locale = null;

    if (this.hasIsoLocale) {
      locale = LandmarkContext.contextSessionKeys[ 'csk.IsoLocale' ];
    } else if (data && data.hasOwnProperty('locale')) {
      locale = data.locale;
    }

    if (!locale) {
      locale = this.defaultLocale;
    }

    // maintain backwards compatibility with 3.5 (csk.IsoLocale=xx_ZZ)
    locale = locale.replace('_', '-');
    return locale;
  }

  private get hasIsoLocale(): boolean {
    return !!(LandmarkContext &&
      LandmarkContext.contextSessionKeys &&
      LandmarkContext.contextSessionKeys[ 'csk.IsoLocale' ]
    );
  }
}

export function LMLocaleInitializerFactory(service: LMLocaleInitializerService) {
  return () => service.initialize();
}
